// 9fbef606107a605d69c0edbcd8029e5d
import PropTypes from 'prop-types';
import logger from '@tcp/core/src/utils/loggerInstance';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels.js';
import {
  getListItem,
  getCarouselDesign,
  getColumnImageUrl,
  getStyliticsLabels,
  getUniqueClassName,
  isAnimationAllowed,
  logOutOfStockItemsOnce,
  getDoubleWidthTwoItems,
  getDoubleWidthSingleItem,
} from '../utils/ShopByLookStylitics.util';
import SkeletonLoader from '../component/SkeletonLoader';
import useFetchStyliticsData from '../hooks/useFetchStyliticsData';
import Carousel from '../../ModuleTabbedCarousel/molecules/Carousel';
import RenderTileView from '../molecules/RenderTileView/views/RenderTileView';
import { MultiItemContainer, CarouselContainer } from '../styles/ShopByLookStylitics.style';

const renderTile = (
  columnData,
  index,
  customImageUrl,
  ref,
  {
    labels,
    itemIndex,
    uniqueKey,
    ICID_PARAM,
    isEspotTile,
    isMobileView,
    activeTileId,
    stylingProps,
    behaviorFlags,
    isFullRowView,
    isAnimatedList,
    animationProps,
    setActiveTileId,
    uniqueClassName,
    isAnimateToLeft,
    isCarouselDesign,
    carouselItemHeight,
    isOneColumnFullRow,
    isNonClickablePromo,
    isCombineImageDesign,
    onQuickViewOpenClick,
    carouselFlipListSize,
    carouselItemImageSize,
    isDoubleWidthTwoItems,
    isDoubleWidthSingleItem,
    adobeAnalyticsProps,
    imageFitTypeOverride,
    shouldAllowMaxHeight,
  }
) => {
  const {
    oosBgColor,
    oosTextColor,
    aspectRatioObj,
    carouselBgColor,
    tileHeightValue,
    tileBadgeHeightValue,
  } = stylingProps;
  const tileHeightNum = tileHeightValue - 30;
  const imageHeightVal = tileHeightNum - 5;
  const { isRedirectToPDP, showOosOverlay } = behaviorFlags;
  const imageClass = isCarouselDesign ? `item-image-${itemIndex || 0}` : `${uniqueClassName}-image`;
  const containerClass = isCarouselDesign
    ? `carousel-item-${itemIndex || 0}`
    : `${uniqueClassName}-container`;

  return (
    <RenderTileView
      itemRef={ref}
      key={columnData.id}
      dataList={columnData}
      uniqueKey={uniqueKey}
      icidParam={ICID_PARAM}
      oosBgColor={oosBgColor}
      isEspotTile={isEspotTile}
      imageClassName={imageClass}
      oosTextColor={oosTextColor}
      isMobileView={isMobileView}
      activeTileId={activeTileId}
      isFullRowView={isFullRowView}
      backToLook={labels.backToLook}
      customImageUrl={customImageUrl}
      isAnimatedList={isAnimatedList}
      animationProps={animationProps}
      aspectRatioObj={aspectRatioObj}
      showOosOverlay={showOosOverlay}
      tileHeightValue={tileHeightNum}
      imageHeightVal={imageHeightVal}
      setActiveTileId={setActiveTileId}
      isRedirectToPDP={isRedirectToPDP}
      carouselBgColor={carouselBgColor}
      isAnimateToLeft={isAnimateToLeft}
      isCarouselDesign={isCarouselDesign}
      containerClassName={containerClass}
      carouseItemHeight={carouselItemHeight}
      shopButtonText={labels.shopButtonText}
      oosOverlayText={labels.oosOverlayText}
      isOneColumnFullRow={isOneColumnFullRow}
      modalHeaderText={labels.modalHeaderText}
      isNonClickablePromo={isNonClickablePromo}
      tileBadgeHeightValue={tileBadgeHeightValue}
      carouselFlipListSize={carouselFlipListSize}
      onQuickViewOpenClick={onQuickViewOpenClick}
      isCombineImageDesign={isCombineImageDesign}
      isDoubleTileGridView={isDoubleWidthTwoItems}
      carouselItemImageSize={carouselItemImageSize}
      isDoubleGridSingleTile={isDoubleWidthSingleItem}
      adobeAnalyticsProps={adobeAnalyticsProps}
      imageFitTypeOverride={imageFitTypeOverride}
      shouldAllowMaxHeight={shouldAllowMaxHeight}
    />
  );
};

const ShopByLookStylitics = ({
  tagValue,
  uniqueKey,
  ICID_PARAM,
  designProps,
  stylingProps,
  columnImages,
  viewTypeFlags,
  behaviorFlags,
  animationProps,
  isShopByLookTView,
  isShopByLookMView,
  styliticsDataState,
  onQuickViewOpenClick,
  fetchShopByLookStyliticsData,
  adobeAnalyticsProps,
  imageFitTypeOverride,
  shouldAllowMaxHeight,
}) => {
  const {
    maxItem,
    isMultiTileDesign,
    isSingleTileDesign,
    isScrollableCarousel,
    isCombineImageDesign,
  } = designProps;
  const { isRedirectToPDP, isNonClickablePromo } = behaviorFlags;
  const { slotName, aspectRatioObj, tileHeightValue } = stylingProps;
  const { error, isLoading, shopByLookStyliticsData } = styliticsDataState;
  const { isFullRowView, isSingleTileView, isDoubleTileView } = viewTypeFlags;
  const { dataList, isDataUnavailable } = useFetchStyliticsData({
    error,
    tagValue,
    uniqueKey,
    count: maxItem,
    isRedirectToPDP,
    shopByLookStyliticsData,
    fetchShopByLookStyliticsData,
    isNonClickablePromo,
  });
  const tileRefs = useRef([]);
  const carouselRef = useRef(null);
  const [isLoader, setIsLoader] = useState(true);
  const [activeTileId, setActiveTileId] = useState(null);
  const labels = useMemo(() => getStyliticsLabels(PLPLabels), []);
  const isCarouselDesign = getCarouselDesign(isScrollableCarousel, isMultiTileDesign);
  const isDoubleWidthTwoItems = getDoubleWidthTwoItems(isDoubleTileView, isMultiTileDesign);
  const isDoubleWidthSingleItem = getDoubleWidthSingleItem(isDoubleTileView, isSingleTileDesign);
  const { firstColumnImage, secondColumnImage, thirdColumnImage, fourthColumnImage } = columnImages;
  const uniqueClassName = getUniqueClassName({
    slotName,
    isFullRowView,
    isDoubleTileView,
    isSingleTileView,
    isSingleTileDesign,
    isCombineImageDesign,
  });

  useEffect(() => {
    logOutOfStockItemsOnce({ isLoading, dataList, uniqueKey, logger, isNonClickablePromo });
    if (!isLoading) {
      setIsLoader(false);
    }
  }, [isLoading, dataList, uniqueKey]);

  if (isLoader || isLoading) {
    return (
      <SkeletonLoader
        isFullRowView={isFullRowView}
        isEspotTile={isSingleTileView}
        aspectRatioObj={aspectRatioObj}
        isCarouselView={isCarouselDesign}
        isDoubleTileGridView={isDoubleWidthTwoItems}
        isDoubleGridSingleTile={isDoubleWidthSingleItem}
      />
    );
  }

  if (isDataUnavailable || error) return null;

  if (dataList.length === 1) {
    tileRefs.current[0] = tileRefs.current[0] || React.createRef();
    return renderTile(dataList[0], 0, firstColumnImage, tileRefs.current[0], {
      labels,
      uniqueKey,
      ICID_PARAM,
      stylingProps,
      behaviorFlags,
      isFullRowView,
      animationProps,
      uniqueClassName,
      onQuickViewOpenClick,
      isDoubleWidthTwoItems,
      isDoubleWidthSingleItem,
      isEspotTile: isSingleTileView,
      isMobileView: isShopByLookTView,
      isOneColumnFullRow: isFullRowView,
      adobeAnalyticsProps,
      imageFitTypeOverride,
      shouldAllowMaxHeight,
      isNonClickablePromo,
    });
  }

  if (!isCarouselDesign) {
    const listItems = getListItem(dataList, isShopByLookTView, isShopByLookMView);
    const isAnimatedList = isAnimationAllowed({
      animationProps,
      isCarouselDesign,
      isSingleTileDesign,
      isCombineImageDesign,
    });
    return (
      <MultiItemContainer
        className={'multi-column-container'}
        aspectRatioObj={aspectRatioObj}
        tileHeightValue={tileHeightValue - 30}
        isCombineImageDesign={isCombineImageDesign}
        isFullRowView={isFullRowView}
        shouldAllowMaxHeight={shouldAllowMaxHeight}
      >
        {listItems.map((column, index) => {
          tileRefs.current[index] = tileRefs.current[index] || React.createRef();
          const customImageUrl = getColumnImageUrl(
            index,
            firstColumnImage,
            secondColumnImage,
            thirdColumnImage,
            fourthColumnImage
          );
          const isAnimateToLeft = index === listItems.length - 1;
          return renderTile(column, index, customImageUrl, tileRefs.current[index], {
            labels,
            uniqueKey,
            ICID_PARAM,
            stylingProps,
            activeTileId,
            behaviorFlags,
            isFullRowView,
            isAnimatedList,
            animationProps,
            uniqueClassName,
            isAnimateToLeft,
            setActiveTileId,
            isCombineImageDesign,
            onQuickViewOpenClick,
            isDoubleWidthTwoItems,
            isDoubleWidthSingleItem,
            isMobileView: isShopByLookTView,
            adobeAnalyticsProps,
            imageFitTypeOverride,
            shouldAllowMaxHeight,
          });
        })}
      </MultiItemContainer>
    );
  }

  const carouselItemHeight = Math.floor(tileHeightValue * 0.8);
  const carouselItemImageSize = Math.floor(tileHeightValue * 0.73);
  const carouselFlipListSize = Math.floor(tileHeightValue * 0.65);

  return (
    <CarouselContainer className={uniqueClassName}>
      <Carousel
        key={`fullWidthTile-carousel`}
        isArrowVisible={dataList.length > 3}
        tileRef={carouselRef}
        defaultTileWidth={365}
        slidesToScroll={3}
        isCategoryTab={false}
        customCls="fullWidthTile-carousel"
        customClassNameOnChildBlock="fullWidthTile-carousel-content"
        customLeftArrowClassName="fullWidthTile-carousel-left-arrow"
        customRightArrowClassName="fullWidthTile-carousel-right-arrow"
      >
        {dataList.map((column, index) => {
          tileRefs.current[index] = tileRefs.current[index] || React.createRef();
          return renderTile(column, index, '', tileRefs.current[index], {
            labels,
            uniqueKey,
            ICID_PARAM,
            stylingProps,
            behaviorFlags,
            isFullRowView,
            animationProps,
            itemIndex: index,
            isCarouselDesign,
            carouselItemHeight,
            onQuickViewOpenClick,
            carouselFlipListSize,
            carouselItemImageSize,
            isMobileView: isShopByLookTView,
            adobeAnalyticsProps,
            imageFitTypeOverride,
            shouldAllowMaxHeight,
          });
        })}
      </Carousel>
    </CarouselContainer>
  );
};

ShopByLookStylitics.propTypes = {
  ICID_PARAM: PropTypes.string,
  isShopByLookMView: PropTypes.bool,
  isShopByLookTView: PropTypes.bool,
  tagValue: PropTypes.string.isRequired,
  uniqueKey: PropTypes.string.isRequired,
  behaviorFlags: PropTypes.shape({
    isRedirectToPDP: PropTypes.bool,
    showOosOverlay: PropTypes.bool,
    isNonClickablePromo: PropTypes.bool,
  }),
  columnImages: PropTypes.shape({
    firstColumnImage: PropTypes.string,
    secondColumnImage: PropTypes.string,
    thirdColumnImage: PropTypes.string,
    fourthColumnImage: PropTypes.string,
  }),
  designProps: PropTypes.shape({
    maxItem: PropTypes.number,
    isMultiTileDesign: PropTypes.bool,
    isSingleTileDesign: PropTypes.bool,
    isScrollableCarousel: PropTypes.bool,
    isCombineImageDesign: PropTypes.bool,
  }),
  stylingProps: PropTypes.shape({
    oosBgColor: PropTypes.string,
    oosTextColor: PropTypes.string,
    carouselBgColor: PropTypes.string,
    aspectRatioObj: PropTypes.shape({}),
    slotName: PropTypes.string.isRequired,
    categoryTag: PropTypes.string.isRequired,
    tileHeightValue: PropTypes.number.isRequired,
    tileBadgeHeightValue: PropTypes.number.isRequired,
  }),
  viewTypeFlags: PropTypes.shape({
    isFullRowView: PropTypes.bool.isRequired,
    isInGridPromo: PropTypes.bool.isRequired,
    isSingleTileView: PropTypes.bool.isRequired,
    isDoubleTileView: PropTypes.bool.isRequired,
  }).isRequired,
  animationProps: PropTypes.shape({
    isFlipAnimation: PropTypes.bool,
    isSlideAnimation: PropTypes.bool,
  }),
  styliticsDataState: PropTypes.shape({
    error: PropTypes.string,
    isLoading: PropTypes.bool,
    shopByLookStyliticsData: PropTypes.shape({}),
  }),
  fetchShopByLookStyliticsData: PropTypes.func,
  onQuickViewOpenClick: PropTypes.func.isRequired,
  shouldAllowMaxHeight: PropTypes.bool,
};

ShopByLookStylitics.defaultProps = {
  ICID_PARAM: '',
  isShopByLookMView: false,
  isShopByLookTView: false,
  behaviorFlags: {
    isRedirectToPDP: false,
    showOosOverlay: false,
    isNonClickablePromo: false,
  },
  stylingProps: {
    oosBgColor: '',
    oosTextColor: '',
    aspectRatioObj: {},
    carouselBgColor: '',
  },
  columnImages: {
    firstColumnImage: '',
    secondColumnImage: '',
    thirdColumnImage: '',
    fourthColumnImage: '',
  },
  designProps: {
    maxItem: 10,
    isMultiTileDesign: false,
    isSingleTileDesign: false,
    isScrollableCarousel: false,
    isCombineImageDesign: false,
  },
  animationProps: {
    isFlipAnimation: false,
    isSlideAnimation: false,
  },
  styliticsDataState: {
    error: '',
    isLoading: false,
    shopByLookStyliticsData: {},
  },
  fetchShopByLookStyliticsData: () => {},
  shouldAllowMaxHeight: false,
};

export default ShopByLookStylitics;
